import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Info } from 'lucide-react';

export const CompOffModal = ({
  open,
  onOpenChange,
  form,
  setForm,
  onSubmit
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Comp-Off</DialogTitle>
          <DialogDescription>
            Request compensatory leave for extra work hours
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Work Date *</Label>
            <Input
              type="date"
              value={form.work_date}
              onChange={(e) => setForm({...form, work_date: e.target.value})}
              data-testid="compoff-date"
            />
          </div>
          <div>
            <Label>Hours Worked</Label>
            <Input
              type="number"
              min="1"
              max="24"
              value={form.hours_worked}
              onChange={(e) => setForm({...form, hours_worked: parseInt(e.target.value) || 8})}
              data-testid="compoff-hours"
            />
          </div>
          <div>
            <Label>Reason / Task *</Label>
            <Textarea
              value={form.reason}
              onChange={(e) => setForm({...form, reason: e.target.value})}
              placeholder="Describe the work performed..."
              rows={3}
              data-testid="compoff-reason"
            />
          </div>
          <div>
            <Label>Project (Optional)</Label>
            <Input
              value={form.project_or_task}
              onChange={(e) => setForm({...form, project_or_task: e.target.value})}
              placeholder="Project or task name"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} data-testid="submit-compoff-btn">Submit Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EncashmentModal = ({
  open,
  onOpenChange,
  form,
  setForm,
  onSubmit
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Leave Encashment</DialogTitle>
          <DialogDescription>
            Convert your unused leave days to cash
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Leave Type *</Label>
            <Select 
              value={form.leave_type} 
              onValueChange={(v) => setForm({...form, leave_type: v})}
            >
              <SelectTrigger data-testid="encash-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="earned">Earned Leave</SelectItem>
                <SelectItem value="casual">Casual Leave</SelectItem>
                <SelectItem value="sick">Sick Leave</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Days to Encash *</Label>
            <Input
              type="number"
              min="1"
              value={form.days_to_encash}
              onChange={(e) => setForm({...form, days_to_encash: parseInt(e.target.value) || 1})}
              data-testid="encash-days"
            />
          </div>
          <div>
            <Label>Remarks (Optional)</Label>
            <Textarea
              value={form.remarks}
              onChange={(e) => setForm({...form, remarks: e.target.value})}
              placeholder="Any additional remarks..."
              rows={2}
            />
          </div>
          
          {/* Balance Info */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 text-blue-700">
              <Info className="w-4 h-4" />
              <span className="text-sm">
                Available balance will be checked against your selected leave type
              </span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} data-testid="submit-encash-btn">Submit Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EnhancementActionModal = ({
  open,
  onOpenChange,
  selectedItem,
  actionType,
  reason,
  setReason,
  paymentRef,
  setPaymentRef,
  onConfirm,
  formatDate
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {actionType === 'approve' ? 'Approve Request' :
             actionType === 'reject' ? 'Reject Request' :
             actionType === 'paid' ? 'Mark as Paid' : 'Process Request'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {selectedItem && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="font-medium">{selectedItem.employee_name}</p>
              <p className="text-sm text-slate-500">
                {selectedItem.type === 'comp_off' 
                  ? `Comp-Off for ${formatDate(selectedItem.work_date)}`
                  : `${selectedItem.days_to_encash} days ${selectedItem.leave_type} leave`}
              </p>
            </div>
          )}
          
          {actionType === 'paid' && (
            <div>
              <Label>Payment Reference</Label>
              <Input
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                placeholder="e.g., TXN123456"
                data-testid="payment-ref"
              />
            </div>
          )}
          
          <div>
            <Label>Remarks {actionType === 'reject' ? '*' : '(Optional)'}</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={actionType === 'reject' ? 'Reason for rejection...' : 'Any remarks...'}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={onConfirm}
            className={
              actionType === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' :
              actionType === 'reject' ? 'bg-red-600 hover:bg-red-700' :
              'bg-blue-600 hover:bg-blue-700'
            }
            data-testid="confirm-enhancement-action"
          >
            {actionType === 'approve' ? 'Approve' :
             actionType === 'reject' ? 'Reject' :
             actionType === 'paid' ? 'Mark Paid' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
